import { Box } from "@mui/material";
import { alpha } from "@mui/material/styles";
import type { FC } from "react";
import { ALPHA, DIFF_REVIEW, RADIUS } from "@/theme/tokens";

/** One run of a search result: `match` runs are emphasized, the rest render plain. */
export type HighlightSegment = { text: string; match: boolean };

/**
 * A half-open `[start, end)` character range into the text being rendered — how a server reports
 * *which* run of a source document a value was read from.
 */
export type HighlightSpan = [start: number, end: number];

/**
 * How a matched run is drawn.
 *
 * - `emphasis` (default) — brand-colored bold ink, no fill. The right weight for a search result,
 *   where the whole string is short and the match is most of it.
 * - `mark` — a tinted `<mark>` wash plus an underline rule. For **provenance**: pointing at the run of
 *   a longer passage that a value was extracted from, which has to be findable inside surrounding
 *   prose rather than merely noticed once the eye is already on it.
 */
export type HighlightVariant = "emphasis" | "mark";

type Props = {
	segments: HighlightSegment[];
	variant?: HighlightVariant;
};

/**
 * Turn server-reported character ranges into the `HighlightSegment[]` this component renders.
 *
 * Overlapping or touching spans are merged — a highlighter that renders two `<mark>`s over the same
 * characters double-tints the overlap, which reads as a third, stronger state that means nothing.
 *
 * Anything that does not fit the text is **dropped, never clamped**: inverted, negative, out-of-range,
 * and — the one that matters — a valid `start` with an `end` past the end of the string. Clamping that
 * last case would highlight from a real word all the way to the end of the passage, which is worse than
 * not highlighting it: a wrong highlight sitting next to an accept checkbox is a false claim about
 * where a value came from, while a missing one just sends the reader to read the passage themselves.
 */
export function segmentsFromSpans(text: string, spans: HighlightSpan[]): HighlightSegment[] {
	const usable = spans
		.filter(([start, end]) => Number.isInteger(start) && Number.isInteger(end) && start >= 0 && end > start && end <= text.length)
		.sort((a, b) => a[0] - b[0]);

	const merged: HighlightSpan[] = [];
	for (const [start, end] of usable) {
		const previous = merged[merged.length - 1];
		if (previous && start <= previous[1]) previous[1] = Math.max(previous[1], end);
		else merged.push([start, end]);
	}

	const segments: HighlightSegment[] = [];
	let cursor = 0;
	for (const [start, end] of merged) {
		if (start > cursor) segments.push({ text: text.slice(cursor, start), match: false });
		segments.push({ text: text.slice(start, end), match: true });
		cursor = end;
	}
	if (cursor < text.length) segments.push({ text: text.slice(cursor), match: false });
	return segments;
}

/**
 * Renders the matched runs of a string emphasized and the rest plain.
 *
 * Color comes from `theme.palette.primary` — never a hardcoded hex — so both variants re-tint with the
 * brand. Generic: any surface that can say *which characters matter* can pass segments (a search
 * result via its own matcher, an extraction's source text via `segmentsFromSpans`).
 */
export const HighlightedText: FC<Props> = ({ segments, variant = "emphasis" }) => {
	let offset = 0;
	return (
		<>
			{segments.map((segment) => {
				// Stable key from the segment's char offset in the label (not the array index).
				const key = `${offset}-${segment.match}`;
				offset += segment.text.length;
				return segment.match ? (
					<Box
						key={key}
						component="mark"
						sx={(theme) =>
							variant === "mark"
								? {
										backgroundColor: alpha(theme.palette.primary.main, ALPHA.SPAN_HIGHLIGHT_TINT),
										// Not colour-alone: the rule survives a grayscale print and a colour-vision deficiency.
										borderBottom: `2px solid ${alpha(theme.palette.primary.main, ALPHA.SPAN_HIGHLIGHT_RULE)}`,
										color: "inherit",
										fontWeight: 600,
										borderRadius: `${RADIUS.HIGHLIGHT}px`,
										padding: DIFF_REVIEW.MARK_PADDING,
										// A highlight that breaks across lines must stay one visual run, not two boxes.
										boxDecorationBreak: "clone",
										WebkitBoxDecorationBreak: "clone",
									}
								: { backgroundColor: "transparent", color: theme.palette.primary.dark, fontWeight: 700, padding: 0 }
						}
					>
						{segment.text}
					</Box>
				) : (
					<span key={key}>{segment.text}</span>
				);
			})}
		</>
	);
};
